<template>
  <div class="infection-case-creator">
  <!-- hidden (not closed) during the post-save prompts: closing would emit creator-close and drop the flow -->
  <el-dialog v-model="dialogVisibility" title="Create Infection Case" :class="{ 'behind-post-save-prompt': postSavePromptVisible }" :close-on-click-modal="false" :close-on-press-escape="false" :show-close="false">
    <el-form v-loading="dialogBusy" ref="editorForm" :model="formModel" :rules="formRules" label-width="120px">
      <!-- Individual Type -->
      <el-form-item v-if="this.isAdminStaff" label="Individual Type" prop="individualType">
        <el-select v-model="formModel.individualType" v-on:change="handleIndividualTypeChange">
          <el-option label="Resident" value="Client"> </el-option>
          <el-option label="Staff" value="Staff"> </el-option>
        </el-select>
      </el-form-item>
      <!-- Facility -->
      <el-form-item v-if="isMultiFacility" label="Facility" prop="facId">
        <el-select v-model="formModel.facId" :disabled="isOutbreak" v-on:change="handleFacilityChange">
          <el-option v-for="facility in facilities" :key="facility.id" :label="facility.displayText" :value="facility.id"></el-option>
        </el-select>
      </el-form-item>
      <!-- Unit -->
      <el-form-item v-if="showUnitFilter" label="Unit" prop="unitId" :disabled="formModel.facId">
        <el-select v-model="formModel.unitId" v-on:change="handleUnitChange">
          <el-option :label="'All'" :value="0"></el-option>
          <el-option v-for="unit in units" :key="unit.id" :label="unit.displayText" :value="unit.id"></el-option>
        </el-select>
      </el-form-item>
      <!-- Search Discharged Toggle -->
      <el-form-item style="margin-bottom: 0" v-if="formModel.individualType === clientTypeMap.Resident">
        <el-checkbox v-model="showDischargedClients" @change="handleDischargedChange"> Search Discharged Residents</el-checkbox>
      </el-form-item>
      <!-- Individual (when NOT showing discharged)-->
      <el-form-item v-if="!showDischargedClients || formModel.individualType !== clientTypeMap.Resident" label="Individual" prop="client">
        <el-select v-model="formModel.client" value-key="id" filterable :disabled="!formModel.facId">
          <el-option v-for="item in filteredClients" :key="item.id" :label="item.nameFormatted" :value="item" />
        </el-select>
      </el-form-item>
      <!-- Search Discharged -->
      <div v-else>
        <el-form-item>
          <div class="discharged-wrapper">
            <div v-if="showDischargedClients" class="discharged-filters">
              <el-input class="discharged-input" placeholder="First Name" v-model="dischargedFilter.firstName" />
              <el-input class="discharged-input" placeholder="Last Name" v-model="dischargedFilter.lastName" />
              <el-button data-testid="search-discharged" class="discharged-input" @click="fetchDischargedClients"> Search </el-button>
            </div>
          </div>
        </el-form-item>
        <!-- Individual (when showing discharged) -->
        <el-form-item label="Individual" prop="client">
          <el-select v-model="formModel.client" value-key="id" filterable :disabled="!formModel.facId">
            <el-option v-for="item in filteredClients" :key="item.id" :label="item.nameFormatted" :value="item" />
          </el-select>
          <span>{{ dischargedResults }}</span>
        </el-form-item>
      </div>
      <!-- Infection Site -->
      <el-form-item label="Infection Site" prop="infectionSite">
        <el-select
          v-model="formModel.infectionSite"
          :disabled="isOutbreak"
          v-on:change="
            configureSymptoms();
            formModel.subtypeString = null;
            formModel.symptoms = [];
          "
        >
          <el-option v-for="symptomType in symptomTypes" :key="symptomType.id" :label="symptomType.displayText" :value="symptomType.type"></el-option>
        </el-select>
      </el-form-item>
      <!-- Infection Site Sub Type -->
      <el-form-item label="Type" prop="subtypeString">
        <el-select v-model="formModel.subtypeString" :disabled="!formModel.infectionSite">
          <el-option v-for="item in subTypes" :key="item" :label="item" :value="item"></el-option>
        </el-select>
      </el-form-item>
      <!-- Symptoms -->
      <el-form-item v-if="showSymptoms" label="Symptoms" prop="symptoms" :disabled="!formModel.infectionSite">
        <el-select v-model="formModel.symptoms" value-key="id" placeholder="Select" multiple filterable no-data-text="No symptoms available" no-match-text="No symptoms available">
          <el-option v-for="item in filteredSymptoms" :label="item.displayText" :key="item.id" :value="item"> </el-option>
        </el-select>
      </el-form-item>
      <!-- Onset Date -->
      <el-form-item label="Onset Date" prop="onsetDate">
        <el-date-picker v-model="formModel.onsetDate" type="date" editable clearable :format="$configStore.datePickerDateFormat()" placeholder="Select date" :picker-options="onsetDatePickerOptions"> </el-date-picker>
      </el-form-item>
      <!-- Origin -->
      <el-form-item label="Origin" prop="origin">
        <el-select v-model="formModel.origin" placeholder="Select">
          <el-option v-for="o in origins" :label="o.label" :key="o.value" :value="o.value"> </el-option>
        </el-select>
      </el-form-item>
      <!-- Confirmed -->
      <el-form-item label="Confirmed?" prop="confirmed">
        <el-switch v-model="formModel.confirmed" on-text="Yes" off-text="No" on-color="#13ce66" off-color="#ff4949"> </el-switch>
      </el-form-item>

      <!-- Precautions-->
      <el-form-item label="Precautions">
        <el-button data-testid="add-precautions" @click="addPrecaution" size="small"> + Add Precautions</el-button>
      </el-form-item>
      <div v-if="showPrecautions">
        <div v-for="(precaution, index) in formModel.precautions" :key="index">
          <el-row class="precaution-row" :gutter="10">
            <el-col :xs="24" :sm="24" :md="24" :lg="5">
              <el-form-item :prop="'precautions.' + index + '.id'" :rules="formRules.precautions.id" label-width="0px">
                <el-select v-model="precaution.id" placeholder="Select">
                  <el-option v-for="p in precautions" :key="p.id" :label="p.displayText" :value="p.id"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="12" :lg="5">
              <el-form-item :prop="'precautions.' + index + '.startDate'" :rules="formRules.precautions.startDate" label-width="0px">
                <el-date-picker v-model="precaution.startDate" placeholder="Start date" type="datetime" value-format="yyyy-MM-ddTHH:mm:ss"></el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="12" :lg="5">
              <el-form-item :prop="'precautions.' + index + '.endDate'" label-width="0px">
                <el-date-picker v-model="precaution.endDate" placeholder="End date" type="datetime" value-format="yyyy-MM-ddTHH:mm:ss" :picker-options="precautionPickerOptions[index]"></el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="24" :lg="8">
              <el-button data-testid="remove-precaution" @click="removePrecaution(index)" size="small">Remove</el-button>
            </el-col>
          </el-row>
        </div>
      </div>
      <div class="dialog-footer">
        <el-button data-testid="cancel-case" @click="cancelForm" :disabled="dialogBusy">Cancel</el-button>
        <el-button data-testid="save-case" type="primary" @click="commitForm" :disabled="dialogBusy">Save</el-button>
      </div>
    </el-form>
  </el-dialog>
  <send-progress-note-dialog
    v-if="pccNote.client"
    :visible="pccNote.visible"
    :client="pccNote.client"
    :infection-case="pccNote.infectionCase"
    :case-id="pccNote.caseId"
    :precautions-lookup="precautionsLookup"
    @closed="onPccNoteClosed"
  />
    <!-- Outbreak detection prompts (siblings, not nested: element-ui 1.4 dialogs are position:absolute + transform) -->
    <outbreak-alert-prompt v-if="outbreakPrompt.visible" :alert="outbreakPrompt.alert" :busy="outbreakPrompt.busy" v-on:dismiss="finishOutbreakFlow" v-on:link-outbreak="linkCaseToOpenOutbreak" v-on:create-outbreak="openOutbreakEditor"></outbreak-alert-prompt>
    <outbreak-editor v-if="outbreakEditor.visible" :title="outbreakEditor.title" :outbreak="outbreakEditor.preset" :show-manage-cases="false" v-on:editor-close="finishOutbreakFlow" v-on:editor-error="onOutbreakEditorError" v-on:editor-save="linkMatchingCasesToOutbreak"></outbreak-editor>
  </div>
</template>
<script>
const validateNotEmpty = (rule, value, callback) => {
  if (!value) {
    callback("This field cannot be empty");
  }
  callback();
};
const validateArrayNotEmpty = (rule, value, callback) => {
  if (!value.length) {
    callback("This field cannot be empty");
  }
  callback();
};
import auth from "../../auth";
import moment from "moment";
import { COMMON_CLASS_TYPE } from "../Shared/symptom.constants";
import util from "../../util";
import SendProgressNoteDialog from "./SendProgressNoteDialog.vue";
import { OutbreakScope } from "../Shared/ruleset.constants";
import OutbreakEditor from "../OM/EditOutbreak.vue";
import OutbreakAlertPrompt from "./OutbreakAlertPrompt.vue";
import { selectableUnits } from "../OM/outbreak-units";
import { OUTBREAK_CREATE_FAILED_MESSAGE, OUTBREAK_EDITOR_TITLE, linkFailureMessage } from "./outbreak-alert.constants";
import { selectAlertToAct } from "./outbreak-alert.selection";
import { PCC_SYNC_STATUS, SYNC_RESULT } from "../../services/pccSyncJobs";

export default {
  name: "infection-case-creator",
  props: ["facId", "outbreakId", "infectionSite"],
  components: {
    SendProgressNoteDialog,
    "outbreak-editor": OutbreakEditor,
    "outbreak-alert-prompt": OutbreakAlertPrompt,
  },
  data() {
    return {
      isOutbreak: false,
      dialogVisibility: true,
      dialogBusy: true,
      showSymptoms: false,
      showUnitFilter: true,
      facilities: [],
      showDischargedClients: false,
      dischargedFilter: {
        firstName: "",
        lastName: "",
        count: null,
      },
      clients: {},
      filteredClients: [],
      units: [],
      symptomTypes: [],
      symptomType: null,
      subTypes: [],
      precautions: [],
      symptoms: [],
      filteredSymptoms: [],
      showPrecautions: false,
      // Outbreak detection flow (HCI-4913). Set after a successful create when one of the API's
      // outbreakAlerts warrants a prompt; creator-save is deferred until the flow ends.
      createdCaseId: null,
      outbreakPrompt: { visible: false, alert: null, busy: false },
      outbreakEditor: { visible: false, linking: false, preset: null, title: OUTBREAK_EDITOR_TITLE },
      onsetDatePickerOptions: {
        disabledDate(input) {
          // disable dates after current date
          // add 12 hours before normalizing to correct element-ui's DST offset bug
          // (date cells are passed as 23:00 previous day after clocks fall back)
          const currentDate = moment().startOf("day");
          const datepickerDate = moment(input).add(12, "hours").startOf("day");
          return datepickerDate.isAfter(currentDate);
        },
      },
      clientTypeMap: {
        Resident: "Client",
        Staff: "Staff",
      },
      origins: [],
      showSymptomsList: ["GI", "Respiratory", "UTI", "Skin"],
      formRules: {
        facId: [{ required: true, validator: validateNotEmpty, message: "This field  is required", trigger: "blur" }],
        individualType: [{ required: true, validator: validateNotEmpty, message: "This field  is required", trigger: "blur" }],
        client: [{ required: true, validator: validateNotEmpty, message: "This field  is required", trigger: "blur" }],
        onsetDate: [{ required: true, validator: validateNotEmpty, message: "This field  is required", trigger: "blur" }],
        infectionSite: [{ required: true, validator: validateNotEmpty, message: "This field  is required", trigger: "blur" }],
        symptoms: [],
        precautions: {
          id: [{ required: true, validator: validateNotEmpty, message: "This field is required", trigger: "blur" }],
          startDate: [{ required: true, validator: validateNotEmpty, message: "This field is required", trigger: "blur" }],
        },
      },
      formModel: {
        facId: null,
        unitId: 0,
        individualType: "Client",
        client: null,
        infectionSite: null,
        subtypeString: null,
        symptoms: [],
        onsetDate: new Date(),
        origin: null,
        confirmed: null,
        precautions: [],
      },
      pccNote: { visible: false, client: null, infectionCase: null, caseId: null },
    };
  },
  computed: {
    // the saved case's PCC note / outbreak prompts are up; the locked form behind them is hidden
    postSavePromptVisible() {
      return this.pccNote.visible || this.outbreakPrompt.visible || this.outbreakEditor.visible || this.outbreakEditor.linking;
    },
    dischargedResults() {
      return this.dischargedFilter.count !== null ? `  ${this.dischargedFilter.count} Resident${this.dischargedFilter.count === 1 ? "" : "s"} found` : "";
    },
    precautionsLookup() {
      return util.arrayToObj(this.precautions, "id");
    },
    precautionPickerOptions() {
      return this.formModel.precautions.map((precaution) => {
        // normalize to midnight so same-day end date is allowed
        const minEndDate = moment(precaution.startDate || undefined).startOf("day");
        return {
          // add 12 hours to correct element-ui's DST offset (cells passed as 23:00 previous day after fallback)
          disabledDate: (time) => moment(time).add(12, "hours").startOf("day").isBefore(minEndDate),
        };
      });
    },
  },
  watch: {
    dialogVisibility(val) {
      if (val === false) {
        this.cancelForm();
      }
    },
  },
  methods: {
    cancelForm() {
      this.$emit("creator-close");
    },
    // --- outbreak detection flow -------------------------------------------------
    // Prompts only when selectAlertToAct found something to act on. In outbreak mode the case
    // was already linked by the outbreak-detail path, so no prompt.
    shouldPromptForOutbreak(alert) {
      return !this.isOutbreak && alert !== null;
    },
    // Every exit of the flow ends here: the case exists, so the parent must refresh.
    finishOutbreakFlow() {
      this.outbreakPrompt.visible = false;
      this.outbreakEditor.visible = false;
      this.outbreakEditor.linking = false;
      this.$emit("creator-save");
    },
    async linkCaseToOpenOutbreak() {
      this.outbreakPrompt.busy = true;
      try {
        await this.linkCasesToOutbreak(this.outbreakPrompt.alert.openOutbreak.id, [this.createdCaseId]);
      } finally {
        this.outbreakPrompt.busy = false;
        this.finishOutbreakFlow();
      }
    },
    openOutbreakEditor() {
      const { threshold, matchingCases } = this.outbreakPrompt.alert;
      this.outbreakEditor.preset = {
        facId: this.formModel.facId,
        // EditOutbreak's site options are labelled by displayText, and outbreak.typeValue stores the label.
        typeValue: this.symptomType.displayText,
        scope: threshold.scope,
        scopeDetail: threshold.scope === OutbreakScope.Unit ? this.unitNamesForCases(matchingCases) : [],
      };
      this.outbreakPrompt.visible = false;
      this.outbreakEditor.visible = true;
    },
    // Display name by id of the units EditOutbreak offers for the case's facility (same filter as
    // its options). The no-unit sentinel and placeholder / archived units are absent, so a case on
    // one of those never yields a pre-selected unit the editor cannot show.
    selectableUnitNames() {
      const facility = this.facilities.find((site) => site.id === this.formModel.facId);
      return new Map(selectableUnits(facility).map((u) => [u.id, u.displayText]));
    },
    // Distinct unit display names for the matching cases.
    unitNamesForCases(matchingCases) {
      const names = this.selectableUnitNames();
      return [...new Set(matchingCases.map((c) => names.get(c.unitId)).filter(Boolean))];
    },
    // The editor lets the user change facility, scope and units before saving, and the outbreak
    // API does not check scope on link, so only the matching cases inside the saved scope are linked.
    casesWithinOutbreakScope(outbreak) {
      const { matchingCases } = this.outbreakPrompt.alert;
      if (outbreak.facId !== this.formModel.facId) return [];
      if (outbreak.scope !== OutbreakScope.Unit) return matchingCases;
      const names = this.selectableUnitNames();
      const scopeUnits = new Set(outbreak.scopeDetail || []);
      return matchingCases.filter((c) => scopeUnits.has(names.get(c.unitId)));
    },
    async linkMatchingCasesToOutbreak(outbreak) {
      // keeps the create form hidden while the link calls run
      this.outbreakEditor.linking = true;
      this.outbreakEditor.visible = false;
      const caseIds = this.casesWithinOutbreakScope(outbreak).map((c) => c.id);
      try {
        await this.linkCasesToOutbreak(outbreak.id, caseIds);
      } finally {
        this.finishOutbreakFlow();
      }
    },
    // One call per case (the outbreak API has no bulk link), sent together since each link is
    // independent. Failures are tallied and reported once: the cases already exist, so the user
    // finishes by hand.
    async linkCasesToOutbreak(outbreakId, caseIds) {
      const linked = await Promise.all(
        caseIds.map((id) =>
          this.$http.post(`${window.CONFIG.om_api}/${outbreakId}/cases/${id}`).then(
            () => true,
            () => false
          )
        )
      );
      const failed = linked.filter((ok) => !ok).length;
      if (failed > 0) {
        this.$message.error(linkFailureMessage(failed, caseIds.length));
      }
    },
    onOutbreakEditorError() {
      this.$message.error(OUTBREAK_CREATE_FAILED_MESSAGE);
      this.finishOutbreakFlow();
    },
    async commitForm() {
      this.$refs.editorForm.validate(async (valid) => {
        if (valid) {
          this.dialogBusy = true;
          const raw = { ...this.formModel };
          // setup infection case body
          const infectionCase = {
            facId: raw.facId,
            unitId: raw.client.unitId || 0,
            bedId: raw.client?.bedId || 0,
            physicianId: 0,
            onsetDate: raw.onsetDate,
            symptomIds: raw.symptoms.map((s) => s.id),
            symptomType: this.symptomType.type,
            infectionSite: raw.infectionSite,
            status: null,
            clientId: raw.client.id,
            organismId: 0,
            resolved: false,
            confirmed: raw.confirmed,
            active: true,
            notifications: (this.$configStore.data.notificationConfigs || []).map((c) => ({
              notificationConfigId: c.id,
              notified: false,
              notifiedDate: null,
            })),
            origin: raw.origin,
            notes: [],
            subtypeString: raw.subtypeString,
          };
          if (!this.symptomType.allowedToMobile) {
            infectionCase.symptomType = null;
            infectionCase.infectionSite = this.symptomType.type;
          }
          // create infection case
          const result = await this.$http.post(`${window.CONFIG.infection_api}/`, JSON.stringify(infectionCase));
          const caseId = result.body.id;
          const pccSyncStatus = result.body.pccSync && result.body.pccSync.status;
          if (pccSyncStatus === PCC_SYNC_STATUS.QUEUED || pccSyncStatus === PCC_SYNC_STATUS.UNCONFIRMED) {
            this.$store.dispatch("trackPccSyncJob", caseId);
          } else if (pccSyncStatus === PCC_SYNC_STATUS.FAILED) {
            this.$store.dispatch("notifyPccSyncResult", SYNC_RESULT.FAILURE);
          }
          const outbreakAlert = selectAlertToAct(result.body.outbreakAlerts);
          // link new infection case to outbreak
          if (this.isOutbreak) {
            await this.$http.post(`${window.CONFIG.om_api}/${this.outbreakId}/cases/${caseId}`);
          }
          // create precautions
          if (raw.precautions.length) {
            const precautions = raw.precautions.map((precaution) => {
              return {
                clientId: raw.client.id,
                symptomDetailId: caseId,
                precautionId: precaution.id,
                startDate: precaution.startDate,
                endDate: precaution.endDate,
              };
            });
            await this.$http.post(`${window.CONFIG.precaution_api}/`, JSON.stringify(precautions));
          }
          if (this.shouldPromptForOutbreak(outbreakAlert)) {
            this.createdCaseId = caseId;
            this.outbreakPrompt.alert = outbreakAlert;
          }
          // PCC note first; any outbreak prompt follows when the note dialog closes
          if (raw.individualType === this.clientTypeMap.Resident && this.$configStore.isPccMarketplaceFacility(raw.facId)) {
            this.pccNote = {
              visible: true,
              client: { ...raw.client, precautionDetails: raw.precautions.map((p) => ({ precautionId: p.id, startDate: p.startDate, endDate: p.endDate })) },
              infectionCase: { ...infectionCase, id: caseId, symptoms: raw.symptoms, onsetDate: raw.onsetDate },
              caseId,
            };
            // the form stays locked (dialogBusy) so the case can't be saved twice
            return; // creator-save is emitted when the note dialog closes
          }
          if (this.outbreakPrompt.alert) {
            // the form stays locked (dialogBusy) so the case can't be saved twice
            this.outbreakPrompt.visible = true;
            return;
          }
          this.$emit("creator-save");
        } else {
          return false;
        }
      });
    },
    onPccNoteClosed() {
      this.pccNote = { visible: false, client: null, infectionCase: null, caseId: null };
      if (this.outbreakPrompt.alert) {
        this.dialogBusy = true; // lock the form again while the outbreak flow runs
        this.outbreakPrompt.visible = true;
        return; // finishOutbreakFlow emits creator-save
      }
      this.$emit("creator-save");
    },
    async fetchActiveClients() {
      const { individualType, facId } = this.formModel;
      this.dialogBusy = true;
      try {
        const statuses = ["C", "T"]; // Current & Transferred
        const fullList = await this.fetchPageOfClients(individualType, statuses, facId);
        fullList.forEach((c) => {
          c.nameFormatted = this.formatClientName(c);
        });
        // store clients in either staff or residents cache
        if (individualType === this.clientTypeMap.Staff) {
          this.clients[facId].staff = fullList;
        } else {
          this.clients[facId].residents = fullList;
        }
      } catch (error) {
        console.error("Error fetching clients:", error);
      } finally {
        this.dialogBusy = false;
      }
    },
    async fetchDischargedClients() {
      const firstName = this.dischargedFilter.firstName.trim();
      const lastName = this.dischargedFilter.lastName.trim();

      // basic validation: at least one field must have >=2 characters
      if (firstName.length < 2 && lastName.length < 2) {
        this.$message.error("Please enter at least two letters in First or Last name.");
        return;
      }

      const { individualType, facId } = this.formModel;
      if (!facId) {
        this.$message.error("Please select a facility.");
        return;
      }

      this.dialogBusy = true;
      try {
        let currentPage = 1;
        let fullList = [];

        while (true) {
          const params = {
            page: currentPage,
            size: 100,
            sortBy: "firstName",
            clientType: individualType,
            status: "D",
            facIds: [facId],
            minimal: true,
          };

          if (firstName) {
            params.firstName = firstName;
          }
          if (lastName) {
            params.lastName = lastName;
          }

          const response = await this.$http.get(window.CONFIG.client_api, { params });
          const pageContent = response.body?.content || [];

          if (!pageContent.length) {
            break;
          }

          fullList = [...fullList, ...pageContent];

          if (pageContent.length < 100) {
            break;
          }
          currentPage++;
        }

        fullList.forEach((c) => {
          c.nameFormatted = this.formatClientName(c);
        });

        // assign to filteredClients directly
        this.filteredClients = fullList;
        this.dischargedFilter.count = fullList.length;
      } catch (error) {
        console.error("Error fetching discharged clients:", error);
      } finally {
        this.dialogBusy = false;
      }
    },

    async fetchPageOfClients(individualType, statuses, facId) {
      let currentPage = 1;
      let fullList = [];

      // loop through pages
      while (true) {
        const params = {
          page: currentPage,
          size: 100,
          sortBy: "firstName",
          clientType: individualType,
          statuses,
          minimal: true,
        };
        if (facId) {
          params.facIds = [facId];
        }

        try {
          const response = await this.$http.get(window.CONFIG.client_api, { params });
          const pageContent = response.body?.content || [];

          if (!pageContent.length) {
            break;
          }

          fullList = [...fullList, ...pageContent];

          if (pageContent.length < 100) {
            break;
          }
          currentPage++;
        } catch (error) {
          console.error("Error fetching clients:", error);
          break;
        }
      }

      return fullList;
    },

    handleIndividualTypeChange() {
      if (this.formModel.individualType === this.clientTypeMap.Staff) {
        this.showDischargedClients = false;
      }
      this.configureUnitFilter();
      this.filterClients();
      this.resetClient();
    },
    handleFacilityChange() {
      this.configureUnitFilter();
      this.filterClients();
      this.resetClient();
    },
    handleUnitChange() {
      this.filterClients();
      this.resetClient();
    },
    handleDischargedChange() {
      this.filteredClients = [];
      this.dischargedFilter = {
        firstName: "",
        lastName: "",
        count: null,
      };
      this.configureUnitFilter();
      this.filterClients();
      this.resetClient();
    },
    // show/hide unit filter based on individualType
    // reset unitId when hidden
    configureUnitFilter() {
      const { individualType, facId } = this.formModel;
      const { showDischargedClients } = this;
      if (individualType === this.clientTypeMap.Resident && facId && !showDischargedClients) {
        this.units = this.facilities.find((site) => site.id === facId).units.filter((unit) => unit.code !== "PLACE_HOLDER" && unit.archived !== true);
        this.showUnitFilter = true;
      } else {
        this.units = [];
        this.showUnitFilter = false;
        this.formModel.unitId = 0;
      }
    },
    addPrecaution() {
      const newPrecaution = {
        id: null,
        startDate: null,
        endDate: null,
      };
      this.$set(this.formModel.precautions, this.formModel.precautions.length, newPrecaution);
      this.showPrecautions = true;
    },
    removePrecaution(index) {
      this.formModel.precautions.splice(index, 1);
      if (this.formModel.precautions.length === 0) {
        this.showPrecautions = false;
      }
    },

    // set client to null when client criteria changes and selected client does not match
    resetClient() {
      const { individualType, client, unitId, facId } = this.formModel;
      const { showDischargedClients } = this;

      if (client) {
        const isDischargedMismatch = (client.status === "D" && !showDischargedClients) || (client.status !== "D" && showDischargedClients);
        const isMismatchedFacility = facId && client.facId !== facId;
        const isMismatchedType = individualType && client.clientType !== individualType;
        const isMismatchedUnit = unitId && client.unitId !== unitId;

        if (isDischargedMismatch || isMismatchedType || isMismatchedUnit || isMismatchedFacility) {
          this.formModel.client = null;
        }
      }
    },
    getPrecautionMinEndDate(precautionIndex) {
      if (!this.formModel.precautions[precautionIndex].startDate) {
        return new Date();
      }
      return new Date(this.formModel.precautions[precautionIndex].startDate);
    },
    async filterClients() {
      const { individualType, unitId, facId } = this.formModel;
      if (this.showDischargedClients) {
        return;
      }
      if (!facId) {
        this.filteredClients = [];
        return;
      }

      const isStaff = individualType === this.clientTypeMap.Staff;
      let source = isStaff ? this.clients[facId].staff : this.clients[facId].residents;

      // fetch clients if source is empty
      if (!source.length) {
        await this.fetchActiveClients();
        source = isStaff ? this.clients[facId].staff : this.clients[facId].residents;
      }
      // filter by unitId if available
      const filteredClients = unitId ? source.filter((client) => client.unitId === unitId) : source;

      this.filteredClients = filteredClients;
    },
    formatClientName(client) {
      return `${this.getClientName(client)} (${this.getClientId(client)})${client.status === "D" ? " - (Discharged)" : ""}`;
    },
    getClientName(client) {
      return `${client.firstName} ${client.middleName ? client.middleName + " " : ""}${client.lastName}`;
    },
    getClientId(client) {
      let id;
      if (client.clientType === "Staff") {
        id = client.staffId;
      } else {
        id = this.$configStore.patientIdType() ? client[this.$configStore.patientIdType()] : client.patientId;
      }
      return id;
    },
    getClientPhoto() {
      const client = this.formModel.client;
      let url = "/static/user.png";
      if (client && client.photo) {
        const filePath = client.photo;
        if (filePath.indexOf("/") !== -1) {
          url = `${window.CONFIG.api}/${filePath}?access_token=${auth.getToken()}`;
        } else {
          url = `/static/${filePath}.png`;
        }
      }
      return url;
    },
    async setupForm() {
      // set individual type = Resident if user cannot add staff cases
      if (!this.canModify) {
        this.formModel.individualType = this.clientTypeMap.Resident;
      }
      if (!this.isMultiFacility) {
        this.formModel.facId = this.facilities[0].id;
      }
      if (!this.isAdminStaff) {
        this.formModel.individualType = this.clientTypeMap.Resident;
      }
      if (this.isOutbreak) {
        this.setupFormForOutbreak();
      }
      this.configureUnitFilter();
    },
    setupFormForOutbreak() {
      this.formModel.facId = this.facId;
      const symptomType = this.symptomTypes.find((item) => this.infectionSite === item.type || this.infectionSite === item.displayText);
      this.formModel.infectionSite = symptomType.type;
      this.configureSymptoms();
    },
    configureSymptoms() {
      const { infectionSite } = this.formModel;
      if (!infectionSite) {
        return;
      }
      // configure symptomType
      const symptomType = this.symptomTypes.find((item) => infectionSite === item.type || infectionSite === item.displayText);
      this.symptomType = symptomType;

      // configure symptoms
      this.showSymptoms = this.showSymptomsList.includes(infectionSite);
      this.filteredSymptoms = this.symptoms.filter((item) => item.classType === infectionSite);
      if (symptomType?.requireSymptoms) {
        this.formRules.symptoms.push({ required: true, validator: validateArrayNotEmpty, message: "This field  is required", trigger: "blur" });
      } else {
        this.formRules.symptoms = [];
      }
      // configure symptom type sub types
      this.subTypes = symptomType?.subTypes || [];
    },
    setupClientCache() {
      const { facilities } = this;
      const clients = {};
      facilities.forEach((facility) => {
        clients[facility.id] = {
          staff: [],
          residents: [],
        };
      });
      this.clients = clients;
    },
  },
  async created() {
    // setup auth
    this.canModify = auth.canModify();
    this.isAdminStaff = auth.userInfo().roles.indexOf("ROLE_ADMIN_STAFF") >= 0;
    this.isOutbreak = !!this.outbreakId;
    this.facilities = this.$configStore.data.sites || [];
    this.isMultiFacility = this.facilities && this.facilities.length > 1;
    this.symptoms = (this.$configStore.data.symptoms || []).filter((s) => s.archived !== true);
    this.precautions = this.$configStore.data.precautions || [];
    this.symptomTypes = (this.$configStore.data.symptomTypes || []).filter((s) => s.type !== COMMON_CLASS_TYPE && s.archived !== true);
    this.origins = this.$configStore.data.origins || [];
    // set origin form rules based on configuration
    const originRequired = this.$configStore.ISOriginRequired();
    if (originRequired) {
      this.formRules.origin = [{ required: true, validator: validateNotEmpty, message: "This field  is required", trigger: "blur" }];
    }
    // apply preset fields & logic
    this.setupClientCache();
    this.setupForm();
    // fetch clients on load if single site user or outbreak; otherwise clients are fetched on facility selection
    if (!this.isMultiFacility || this.isOutbreak) {
      await this.filterClients();
    }
    this.dialogBusy = false;
  },
};
</script>
<style>
/* on the wrapper (el-dialog root): it is full screen with overflow auto, so hiding only .el-dialog left its scrollbar */
.el-dialog__wrapper.behind-post-save-prompt {
  visibility: hidden;
}
.discharged-filters {
  margin-top: 0.5em;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
}
.discharged-input {
  width: 40%;
}
div.profile-photo {
  width: 10em;
  height: 10em;
  border-radius: 50%;
  position: absolute;
  right: 2.5em;
  top: 0.5em;
  display: block;
  overflow: hidden;
  border-radius: 50%;
}

.profile-photo img {
  width: auto;
  height: 100%;
}
.precaution-row .el-input__inner,
.precaution-row .el-select,
.precaution-row .el-select .el-input__inner,
.precaution-row .el-date-editor.el-input,
.precaution-row .el-date-picker .el-input__inner {
  width: 100%;
  box-sizing: border-box;
}
</style>
